import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useMe } from '../hooks/useMe';
import { useI18n } from '../i18n';
import api from '../services/api';

const ROLES = ['admin', 'sales', 'cs_agent'];

const looksLikeSub = (email) => /^(auth0|google-oauth2|windowslive)\|/.test(email || '');

function Section({ title, count, children }) {
  return (
    <Box sx={{ mb: 2.5 }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.8, mb: 1,
        fontSize: 11.5, fontWeight: 600, letterSpacing: '0.09em',
        textTransform: 'uppercase', color: '#847782',
      }}>
        {title}
        {count != null && <Box component="span" sx={{ color: '#FF6666', fontSize: 13 }}>{count}</Box>}
      </Box>
      <Box sx={{ border: '1px solid #E4E0E2', borderRadius: '5px', backgroundColor: '#fff', overflow: 'hidden' }}>
        {children}
      </Box>
    </Box>
  );
}

export default function UsersConsole() {
  const { me } = useMe();
  const { t, tApiError, fmtDate, fmtDateTime } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [q, setQ] = useState('');

  const load = useCallback(() => {
    api.get('/api/admin/users')
      .then((res) => { setData(res.data); setError(null); })
      .catch((err) => setError(
        err.response?.status === 403
          ? t('usersConsole.forbidden')
          : t('usersConsole.loadError')
      ));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  async function save(user, changes) {
    setBusy(user.email);
    try {
      await api.patch('/api/admin/users', {
        email: user.email,
        display_name: user.display_name,
        auth0_user_id: user.auth0_user_id,
        ...changes,
      });
      load();
    } catch (err) {
      setError(tApiError(err, 'usersConsole.saveError'));
    } finally {
      setBusy(null);
    }
  }

  const filtered = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.users;
    return data.users.filter((u) =>
      `${u.email} ${u.display_name}`.toLowerCase().includes(needle));
  }, [data, q]);

  if (error && !data) return <Alert severity="error">{error}</Alert>;
  if (!data) return <Skeleton variant="rectangular" height={320} />;

  const withAccess = filtered.filter((u) => u.access_granted);
  const pending = filtered.filter((u) => !u.access_granted);
  const requests = data.requests || [];
  const admins = data.users.filter((u) => u.access_granted && u.app_role === 'admin').length;

  async function admit(request, role) {
    setBusy(request.auth0_user_id);
    try {
      await api.patch('/api/admin/users', {
        email: request.email || request.auth0_user_id,
        display_name: request.display_name || request.email,
        auth0_user_id: request.auth0_user_id,
        app_role: role,
        access_granted: true,
      });
      load();
    } catch {
      setError(t('usersConsole.admitError'));
    } finally {
      setBusy(null);
    }
  }

  async function dismiss(request) {
    setBusy(request.auth0_user_id);
    try {
      await api.delete(`/api/admin/requests/${encodeURIComponent(request.auth0_user_id)}`);
      load();
    } catch {
      setError(t('usersConsole.dismissError'));
    } finally {
      setBusy(null);
    }
  }

  const Row = ({ u }) => {
    const isSelf = me?.email === u.email;
    const lastAdmin = isSelf && admins <= 1;
    return (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1.7fr 160px auto' },
        gap: 1.5, alignItems: 'center', px: 2, py: 1.4,
        borderBottom: '1px solid #F0ECEE',
        '&:last-of-type': { borderBottom: 'none' },
      }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.8 }}>
            {u.display_name}
            {isSelf && (
              <Box component="span" sx={{ color: '#847782', fontWeight: 400, fontSize: 12 }}>
                {t('usersConsole.self')}
              </Box>
            )}
            {u.blocked && (
              <Chip size="small" label={t('usersConsole.blocked')}
                sx={{ height: 18, fontSize: 10, backgroundColor: '#AE5B61', color: '#fff', fontWeight: 600 }} />
            )}
          </Typography>
          <Typography sx={{
            fontSize: 12, color: looksLikeSub(u.email) ? '#AE5B61' : '#847782',
            overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {looksLikeSub(u.email) ? t('usersConsole.noEmailInToken') : u.email}
            {' · '}{t('usersConsole.lastAccess', {
              date: u.last_login ? fmtDate(u.last_login) : t('common.never'),
            })}
            {u.logins_count != null && ` · ${t('usersConsole.loginCount', { count: u.logins_count })}`}
            {!u.in_auth0 && ` · ${t('usersConsole.notInAuth0')}`}
          </Typography>
        </Box>

        <Tooltip title={t(`roleSummary.${u.app_role || 'cs_agent'}`)} placement="top">
          <TextField
            select size="small" variant="standard"
            value={u.app_role || 'cs_agent'}
            disabled={busy === u.email || lastAdmin}
            onChange={(e) => save(u, { app_role: e.target.value })}
            sx={{ '& .MuiSelect-select': { fontSize: 13, fontFamily: 'Saira' } }}
          >
            {ROLES.map((role) => (
              <MenuItem key={role} value={role} sx={{ fontSize: 13 }}>{t(`roles.${role}`)}</MenuItem>
            ))}
          </TextField>
        </Tooltip>

        <Tooltip title={lastAdmin ? t('usersConsole.lastAdminTooltip') : ''}>
          <span>
            <Button
              size="small"
              variant={u.access_granted ? 'outlined' : 'contained'}
              disabled={busy === u.email || lastAdmin}
              onClick={() => save(u, { access_granted: !u.access_granted })}
              sx={{
                fontSize: 12, minWidth: 108,
                ...(u.access_granted && { color: '#AE5B61', borderColor: '#AE5B61' }),
              }}
            >
              {u.access_granted ? t('usersConsole.revoke') : t('usersConsole.grant')}
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };

  return (
    <Box>
      <Typography sx={{ color: '#847782', fontSize: 13.5, mb: 2, maxWidth: '72ch' }}>
        {t('usersConsole.intro')}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {!data.auth0_configured && (
        <Alert severity="info" sx={{ mb: 2, fontSize: 13 }}>
          {t('usersConsole.auth0NotConfigured', {
            read: 'read:users',
            id: 'AUTH0_MGMT_CLIENT_ID',
            secret: 'AUTH0_MGMT_CLIENT_SECRET',
          })}
        </Alert>
      )}
      {data.auth0_error && <Alert severity="warning" sx={{ mb: 2, fontSize: 13 }}>{data.auth0_error}</Alert>}

      <TextField
        size="small" fullWidth placeholder={t('usersConsole.search')}
        value={q} onChange={(e) => setQ(e.target.value)}
        sx={{ mb: 2.5, backgroundColor: '#fff' }}
      />

      {requests.length > 0 && (
        <Section title={t('usersConsole.requests')} count={requests.length}>
          {requests.map((r) => (
            <Box key={r.auth0_user_id} sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1.7fr 160px auto' },
              gap: 1.5, alignItems: 'center', px: 2, py: 1.4,
              backgroundColor: '#FFF8F8',
              borderBottom: '1px solid #F0ECEE',
              '&:last-of-type': { borderBottom: 'none' },
            }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
                  {r.display_name || r.email || t('usersConsole.unnamedUser')}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#847782' }}>
                  {r.email || (
                    <Box component="span" sx={{ color: '#AE5B61' }}>
                      {t('usersConsole.noEmailWithId', { id: r.auth0_user_id })}
                    </Box>
                  )}
                  {' · '}{t(r.attempts === 1 ? 'usersConsole.attemptOne' : 'usersConsole.attemptMany',
                    { count: r.attempts })}
                  {' · '}{t('usersConsole.lastSeen', { when: fmtDateTime(r.last_seen_at) })}
                </Typography>
              </Box>

              <TextField
                select size="small" variant="standard" defaultValue="cs_agent"
                disabled={busy === r.auth0_user_id}
                onChange={(e) => admit(r, e.target.value)}
                sx={{ '& .MuiSelect-select': { fontSize: 13, fontFamily: 'Saira' } }}
              >
                {ROLES.map((role) => (
                  <MenuItem key={role} value={role} sx={{ fontSize: 13 }}>
                    {t('usersConsole.admitAs', { role: t(`roles.${role}`) })}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small" variant="contained"
                  disabled={busy === r.auth0_user_id}
                  onClick={() => admit(r, 'cs_agent')}
                  sx={{ fontSize: 12 }}
                >{t('usersConsole.admit')}</Button>
                <Button
                  size="small" variant="text"
                  disabled={busy === r.auth0_user_id}
                  onClick={() => dismiss(r)}
                  sx={{ fontSize: 12, color: '#847782' }}
                >{t('usersConsole.dismiss')}</Button>
              </Box>
            </Box>
          ))}
        </Section>
      )}

      <Section title={t('usersConsole.withAccess')} count={withAccess.length}>
        {withAccess.length === 0
          ? <Box sx={{ p: 2.5, color: '#847782', fontSize: 13.5 }}>{t('usersConsole.noMatches')}</Box>
          : withAccess.map((u) => <Row key={u.email} u={u} />)}
      </Section>

      <Section title={t('usersConsole.registeredNoAccess')} count={pending.length}>
        {pending.length === 0
          ? <Box sx={{ p: 2.5, color: '#847782', fontSize: 13.5 }}>
              {t('usersConsole.allHaveAccess')}
            </Box>
          : pending.map((u) => <Row key={u.email} u={u} />)}
      </Section>

      <Section title={t('usersConsole.rolesTitle')}>
        {ROLES.map((key) => (
          <Box key={key} sx={{
            display: 'flex', gap: 1.5, px: 2, py: 1.2, alignItems: 'baseline',
            borderBottom: '1px solid #F0ECEE', '&:last-of-type': { borderBottom: 'none' },
          }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 500, minWidth: 116 }}>
              {t(`roles.${key}`)}
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#847782' }}>{t(`roleSummary.${key}`)}</Typography>
          </Box>
        ))}
      </Section>

      {data.activity?.length > 0 && (
        <Section title={t('usersConsole.activity')}>
          {data.activity.map((a, i) => (
            <Box key={i} sx={{
              px: 2, py: 1.1, fontSize: 12.5, color: '#847782',
              borderBottom: '1px solid #F0ECEE', '&:last-of-type': { borderBottom: 'none' },
            }}>
              <Box component="span" sx={{ color: '#170312', fontWeight: 500 }}>
                {a.actor_name || t('usersConsole.someone')}
              </Box>
              {' '}{t(`usersConsole.actions.${a.action}`) === `usersConsole.actions.${a.action}`
                ? a.action
                : t(`usersConsole.actions.${a.action}`)}{' '}
              <Box component="span" sx={{ color: '#170312', fontWeight: 500 }}>
                {a.target_name || a.target_email}
              </Box>
              {a.detail && ` (${a.detail})`}
              {' · '}{fmtDateTime(a.created_at)}
            </Box>
          ))}
        </Section>
      )}
    </Box>
  );
}
